import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserBehaviorMetrics {
  totalActiveUsers: number;
  dailyCheckins: number;
  weeklyCheckins: number;
  monthlyCheckins: number;
  averageWaitTime: number;
  topCrossings: Array<{ crossing: string; count: number }>;
  checkinHeatmap: Array<{ hour: number; day: string; count: number }>;
}

interface UserDemographics {
  totalUsers: number;
  crossingTypes: Array<{ type: string; count: number }>;
  repeatUsers: number;
  oneTimeUsers: number;
}

interface PromoEngagement {
  totalRedemptions: number;
  totalPromotions: number;
  mostViewedPromos: Array<{ company_name: string; views: number }>;
  mostRedeemedPromos: Array<{ company_name: string; redemptions: number }>;
  averagePointsSpent: number;
}

interface AdvertiserOverview {
  totalAdvertisers: number;
  totalSpend: number;
  advertiserSpending: Array<{ company_name: string; spend: number }>;
  impressionsVsRedemptions: Array<{ company_name: string; impressions: number; redemptions: number }>;
}

export const useAdminAnalytics = (testMode: boolean = false) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userBehavior, setUserBehavior] = useState<UserBehaviorMetrics>({
    totalActiveUsers: 0,
    dailyCheckins: 0,
    weeklyCheckins: 0,
    monthlyCheckins: 0,
    averageWaitTime: 0,
    topCrossings: [],
    checkinHeatmap: []
  });
  const [demographics, setDemographics] = useState<UserDemographics>({
    totalUsers: 0,
    crossingTypes: [],
    repeatUsers: 0,
    oneTimeUsers: 0
  });
  const [promoEngagement, setPromoEngagement] = useState<PromoEngagement>({
    totalRedemptions: 0,
    totalPromotions: 0,
    mostViewedPromos: [],
    mostRedeemedPromos: [],
    averagePointsSpent: 0
  });
  const [advertiserOverview, setAdvertiserOverview] = useState<AdvertiserOverview>({
    totalAdvertisers: 0,
    totalSpend: 0,
    advertiserSpending: [],
    impressionsVsRedemptions: []
  });

  const fetchUserBehaviorMetrics = async () => {
    try {
      const now = new Date();
      const oneDay = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Total active users (users who checked in within the last 30 days)
      const { count: activeUsers } = await supabase
        .from('checkins')
        .select('user_id', { count: 'exact', head: true })
        .gte('created_at', oneMonth.toISOString())
        .not('user_id', 'is', null)
        .eq('is_test', testMode);

      // Daily, weekly, monthly checkins
      const { count: dailyCheckins } = await supabase
        .from('checkins')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneDay.toISOString())
        .eq('is_test', testMode);

      const { count: weeklyCheckins } = await supabase
        .from('checkins')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneWeek.toISOString())
        .eq('is_test', testMode);

      const { count: monthlyCheckins } = await supabase
        .from('checkins')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneMonth.toISOString())
        .eq('is_test', testMode);

      // Average wait time (checkins with checkout time)
      const { data: waitTimeData } = await supabase
        .from('checkins')
        .select('created_at, check_out_time')
        .not('check_out_time', 'is', null)
        .gte('created_at', oneMonth.toISOString())
        .eq('is_test', testMode);

      let averageWaitTime = 0;
      if (waitTimeData?.length) {
        const totalWaitTime = waitTimeData.reduce((sum, checkin) => {
          const waitTime = new Date(checkin.check_out_time!).getTime() - new Date(checkin.created_at).getTime();
          return sum + (waitTime / (1000 * 60)); // Convert to minutes
        }, 0);
        averageWaitTime = Math.round(totalWaitTime / waitTimeData.length);
      }

      // Top crossings
      const { data: crossingData } = await supabase
        .from('checkins')
        .select('garita')
        .gte('created_at', oneMonth.toISOString())
        .eq('is_test', testMode);

      const crossingCounts: { [key: string]: number } = {};
      crossingData?.forEach(checkin => {
        crossingCounts[checkin.garita] = (crossingCounts[checkin.garita] || 0) + 1;
      });

      const topCrossings = Object.entries(crossingCounts)
        .map(([crossing, count]) => ({ crossing, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      // Checkin heatmap data
      const { data: heatmapData } = await supabase
        .from('checkins')
        .select('created_at')
        .gte('created_at', oneWeek.toISOString())
        .eq('is_test', testMode);

      const heatmapCounts: { [key: string]: number } = {};
      heatmapData?.forEach(checkin => {
        const date = new Date(checkin.created_at);
        const hour = date.getHours();
        const day = date.toLocaleDateString('en-US', { weekday: 'short' });
        const key = `${day}-${hour}`;
        heatmapCounts[key] = (heatmapCounts[key] || 0) + 1;
      });

      const checkinHeatmap = Object.entries(heatmapCounts).map(([key, count]) => {
        const [day, hour] = key.split('-');
        return { hour: parseInt(hour), day, count };
      });

      setUserBehavior({
        totalActiveUsers: activeUsers || 0,
        dailyCheckins: dailyCheckins || 0,
        weeklyCheckins: weeklyCheckins || 0,
        monthlyCheckins: monthlyCheckins || 0,
        averageWaitTime,
        topCrossings,
        checkinHeatmap
      });
    } catch (error) {
      console.error('Error fetching user behavior metrics:', error);
    }
  };

  const fetchDemographics = async () => {
    try {
      // Total users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('is_test', testMode);

      // Crossing types breakdown
      const { data: crossingTypesData } = await supabase
        .from('checkins')
        .select('lane_type')
        .eq('is_test', testMode);

      const crossingTypeCounts: { [key: string]: number } = {};
      crossingTypesData?.forEach(checkin => {
        crossingTypeCounts[checkin.lane_type] = (crossingTypeCounts[checkin.lane_type] || 0) + 1;
      });

      const crossingTypes = Object.entries(crossingTypeCounts)
        .map(([type, count]) => ({ type, count }));

      // Repeat vs one-time users
      const { data: userCheckinsData } = await supabase
        .from('checkins')
        .select('user_id')
        .eq('is_test', testMode);

      const userCheckinCounts: { [key: string]: number } = {};
      userCheckinsData?.forEach(checkin => {
        if (checkin.user_id) {
          userCheckinCounts[checkin.user_id] = (userCheckinCounts[checkin.user_id] || 0) + 1;
        }
      });

      const repeatUsers = Object.values(userCheckinCounts).filter(count => count > 1).length;
      const oneTimeUsers = Object.values(userCheckinCounts).filter(count => count === 1).length;

      setDemographics({
        totalUsers: totalUsers || 0,
        crossingTypes,
        repeatUsers,
        oneTimeUsers
      });
    } catch (error) {
      console.error('Error fetching demographics:', error);
    }
  };

  const fetchPromoEngagement = async () => {
    try {
      // Total redemptions and promotions
      const { count: totalRedemptions } = await supabase
        .from('redemptions')
        .select('*', { count: 'exact', head: true })
        .eq('is_test', testMode);

      const { count: totalPromotions } = await supabase
        .from('ads')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('is_test', testMode);

      // Most viewed promotions (using impressions as proxy)
      const { data: viewedPromosData } = await supabase
        .from('ads')
        .select('company_name, impressions')
        .eq('is_active', true)
        .eq('is_test', testMode)
        .order('impressions', { ascending: false })
        .limit(5);

      const mostViewedPromos = viewedPromosData?.map(promo => ({
        company_name: promo.company_name,
        views: promo.impressions || 0
      })) || [];

      // Most redeemed promotions (from redemptions table)
      const { data: redeemedPromosData } = await supabase
        .from('redemptions')
        .select('reward_name')
        .eq('is_test', testMode);

      const redemptionCounts: { [key: string]: number } = {};
      redeemedPromosData?.forEach(redemption => {
        redemptionCounts[redemption.reward_name] = (redemptionCounts[redemption.reward_name] || 0) + 1;
      });

      const mostRedeemedPromos = Object.entries(redemptionCounts)
        .map(([company_name, redemptions]) => ({ company_name, redemptions }))
        .sort((a, b) => b.redemptions - a.redemptions)
        .slice(0, 5);

      // Average points spent
      const { data: pointsData } = await supabase
        .from('redemptions')
        .select('points_used')
        .eq('is_test', testMode);

      const totalPointsSpent = pointsData?.reduce((sum, redemption) => sum + (redemption.points_used || 0), 0) || 0;
      const averagePointsSpent = pointsData?.length ? Math.round(totalPointsSpent / pointsData.length) : 0;

      setPromoEngagement({
        totalRedemptions: totalRedemptions || 0,
        totalPromotions: totalPromotions || 0,
        mostViewedPromos,
        mostRedeemedPromos,
        averagePointsSpent
      });
    } catch (error) {
      console.error('Error fetching promo engagement:', error);
    }
  };

  const fetchAdvertiserOverview = async () => {
    try {
      // Total advertisers and spend
      const { count: totalAdvertisers } = await supabase
        .from('advertisers')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('is_test', testMode);

      const { data: spendData } = await supabase
        .from('advertisers')
        .select('company_name, total_ad_spend')
        .eq('is_active', true)
        .eq('is_test', testMode);

      const totalSpend = spendData?.reduce((sum, advertiser) => sum + (advertiser.total_ad_spend || 0), 0) || 0;

      const advertiserSpending = spendData?.map(advertiser => ({
        company_name: advertiser.company_name,
        spend: advertiser.total_ad_spend || 0
      })).sort((a, b) => b.spend - a.spend) || [];

      // Impressions vs redemptions
      const { data: adsData } = await supabase
        .from('ads')
        .select('company_name, impressions, clicks')
        .eq('is_active', true)
        .eq('is_test', testMode);

      const impressionsVsRedemptions = adsData?.map(ad => ({
        company_name: ad.company_name,
        impressions: ad.impressions || 0,
        redemptions: ad.clicks || 0
      })) || [];

      setAdvertiserOverview({
        totalAdvertisers: totalAdvertisers || 0,
        totalSpend,
        advertiserSpending,
        impressionsVsRedemptions
      });
    } catch (error) {
      console.error('Error fetching advertiser overview:', error);
    }
  };

  const fetchAllAnalytics = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      await Promise.all([
        fetchUserBehaviorMetrics(),
        fetchDemographics(),
        fetchPromoEngagement(),
        fetchAdvertiserOverview()
      ]);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, [user, testMode]);

  return {
    loading,
    userBehavior,
    demographics,
    promoEngagement,
    advertiserOverview,
    refreshAnalytics: fetchAllAnalytics
  };
};